import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import type { NavItem } from './Sidebar'

// A plain path (`/library`) is active on any sub-path. A path carrying a query
// string is active only when every requested param matches, so Skills and
// Connections do not both read as active on any Settings tab.
function isNavItemActive(location: { pathname: string; search: string }, item: NavItem): boolean {
  const [hrefPath, hrefQuery] = item.href.split('?')
  if (!hrefQuery) {
    return [hrefPath, ...(item.alsoActiveOn ?? [])].some((p) => location.pathname.startsWith(p))
  }
  if (location.pathname !== hrefPath) return false
  const current = new URLSearchParams(location.search)
  const wanted = new URLSearchParams(hrefQuery)
  return Array.from(wanted.entries()).every(([key, value]) => current.get(key) === value)
}

function PageRow({ item }: { item: NavItem }) {
  const location = useLocation()
  const isActive = isNavItemActive(location, item)
  const Icon = item.icon
  const showBadge = typeof item.badgeCount === 'number' && item.badgeCount > 0

  return (
    <Link
      to={item.href}
      data-tour={`nav-${item.href}`}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'flex h-12 shrink-0 items-center gap-3 rounded-[10px] px-2.5 text-[0.9375rem] transition-colors md:h-10 md:gap-2.5 md:text-sm',
        'hover:bg-sidebar-accent/60',
        isActive ? 'bg-sidebar-accent font-medium' : 'text-sidebar-foreground'
      )}
    >
      <Icon className="text-muted-foreground size-5 shrink-0 md:size-[17px]" />
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {showBadge && (
        <span className="bg-primary text-primary-foreground inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold">
          {item.badgeCount}
        </span>
      )}
    </Link>
  )
}

interface PageListProps {
  items: NavItem[]
}

/** The Pages segment: one row per destination, shared by the desktop sidebar and the mobile home. */
export function PageList({ items }: PageListProps) {
  return (
    <nav
      aria-label="Pages"
      data-tour="nav-root"
      data-testid="page-list"
      className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto"
    >
      {items.map((item) => (
        <PageRow key={item.href} item={item} />
      ))}
    </nav>
  )
}
